import { ChangeEvent, memo, MutableRefObject, useCallback, useMemo } from "react";
import classNames from "classnames";
import pluralize from "pluralize";
import { SelectInstance } from "react-select";
import { DateField, defaultTimeValueEndOfDay } from "../../DateField";
import { NumberField } from "../../NumberField";
import { SimpleSelect } from "../../SimpleSelect";
import { TimeField } from "../../TimeField";
import { DateTimeRange, DateTimeRangeMode, DateTimeRangePeriod, defaultDateTimeRangeValue } from "../DateTimeRangeType";
import Option from "./Option";

import styles from "./style.module.scss";

export type DateTimeRangeCardProps = {
  value?: DateTimeRange | null,
  onChange?: (value?: DateTimeRange | null) => void,
  timeFormat?: string,
  dateFormat?: string,
  dateTimeFormat?: string,
  serverDateTimeFormat?: string,
  timeZone?: string,
  allowEmpty?: boolean,
  selectRef?: MutableRefObject<SelectInstance<any, boolean, any> | undefined>
}

export const DateTimeRangeCard = memo<DateTimeRangeCardProps>((
  {
    value,
    onChange,
    timeFormat,
    dateFormat,
    timeZone,
    serverDateTimeFormat,
    allowEmpty,
    selectRef,
  }) => {
  const changeValueProperty = useCallback((prop: { [key: string]: any }) => {
    return onChange?.({
      ...value || defaultDateTimeRangeValue,
      ...prop,
    });
  }, [ value, onChange ]);

  const onCheckedChange = (mode: DateTimeRangeMode) => {
    return (e?: ChangeEvent<HTMLInputElement>, checked?: boolean) => {
      if (checked) {
        return changeValueProperty({ mode: DateTimeRangeMode[mode] as keyof typeof DateTimeRangeMode });
      }
    };
  };
  const changeQuantity = (v?: number | null) => changeValueProperty({ amount: v as any });
  const changePeriod = (v: null | (keyof typeof DateTimeRangePeriod) | (ReadonlyArray<keyof typeof DateTimeRangePeriod>)) => changeValueProperty({ period: v });
  const changeFrom = (from: string | null) => changeValueProperty({ from });
  const changeTo = (to: string | null) => changeValueProperty({ to });
  const changeDate = (date: string | null) => changeValueProperty({ date });

  const amount = value?.amount;
  const selectValue = (value?.period === undefined ? DateTimeRangePeriod[DateTimeRangePeriod.days] : value.period) as keyof typeof DateTimeRangePeriod;
  const selectOptions = useMemo(
    () => Object.keys(DateTimeRangePeriod)
      .filter((key: any) => !isNaN(Number(DateTimeRangePeriod[key]))) as (keyof typeof DateTimeRangePeriod)[],
    [],
  );


  return (
    <div className={styles.root} tabIndex={-1}>
      <Option
        label="Day"
        checked={!value?.mode || value?.mode === DateTimeRangeMode[DateTimeRangeMode.date]}
        autoFocus={!value?.mode || value?.mode === DateTimeRangeMode[DateTimeRangeMode.date]}
        onCheckedChange={onCheckedChange(DateTimeRangeMode.date)}>
        <DateField
          className={styles.dateField}
          label="Date"
          value={value?.date || undefined}
          onChange={changeDate}
          dateFormat={dateFormat}
          timeFormat={timeFormat}
          timeZone={timeZone}
          serverDateTimeFormat={serverDateTimeFormat}
          defaultServerDateTimeFormatMode="datetime"
          keepTimeValue
        />
      </Option>
      <Option
        label="Range"
        checked={value?.mode === DateTimeRangeMode[DateTimeRangeMode.range]}
        autoFocus={value?.mode === DateTimeRangeMode[DateTimeRangeMode.range]}
        onCheckedChange={onCheckedChange(DateTimeRangeMode.range)}>
        <div className={classNames(styles.rangeBlock, styles.fromBlock)}>
          <span className={styles.lineTitle}>From:</span>
          <DateField
            className={styles.dateField}
            label="Date"
            value={value?.from || undefined}
            onChange={changeFrom}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            timeZone={timeZone}
            serverDateTimeFormat={serverDateTimeFormat}
            defaultServerDateTimeFormatMode="datetime"
            keepTimeValue
          />
          <TimeField
            className={styles.timeField}
            label="Time"
            value={value?.from}
            isClearable={false}
            onChange={changeFrom}
            timeFormat={timeFormat}
            timeZone={timeZone}
            serverDateTimeFormat={serverDateTimeFormat}
          />
        </div>
        <div className={styles.rangeBlock}>
          <span className={styles.lineTitle}>To:</span>
          <DateField
            className={styles.dateField}
            label="Date"
            value={value?.to || undefined}
            onChange={changeTo}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            timeZone={timeZone}
            serverDateTimeFormat={serverDateTimeFormat}
            defaultTimeValue={defaultTimeValueEndOfDay}
            defaultServerDateTimeFormatMode="datetime"
            keepTimeValue
          />
          <TimeField
            className={styles.timeField}
            label="Time"
            value={value?.to}
            isClearable={false}
            onChange={changeTo}
            timeFormat={timeFormat}
            timeZone={timeZone}
            serverDateTimeFormat={serverDateTimeFormat}
          />
        </div>
      </Option>
      <Option
        label="Within the last"
        autoFocus={value?.mode === DateTimeRangeMode[DateTimeRangeMode.last]}
        checked={value?.mode === DateTimeRangeMode[DateTimeRangeMode.last]}
        onCheckedChange={onCheckedChange(DateTimeRangeMode.last)}>
        <NumberField
          label="Quantity"
          type="number"
          min={1}
          className={styles.quantityField}
          value={amount}
          precision={0}
          onChange={changeQuantity}
        />
        <SimpleSelect
          label=" "
          className={styles.periodField}
          selectRef={selectRef as any}
          value={selectValue}
          options={selectOptions}
          onChange={changePeriod}
          isSearchable={false}
          setOptionLabel={useCallback((option: string) => {
            const str = pluralize(option, amount || 1);
            return str.charAt(0)
              .toUpperCase() + str.slice(1)
              .toLowerCase();
          }, [ amount ])}
        />
      </Option>
      {
        allowEmpty &&
        <Option
          label="Empty"
          autoFocus={value?.mode === DateTimeRangeMode[DateTimeRangeMode.empty]}
          checked={value?.mode === DateTimeRangeMode[DateTimeRangeMode.empty]}
          onCheckedChange={onCheckedChange(DateTimeRangeMode.empty)}>
        </Option>
      }
    </div>
  );
});

export default DateTimeRangeCard;
